
import { useState, useEffect, useMemo } from 'react';
import { supabase } from "@/integrations/supabase/client";
import { toast } from 'sonner';
import { parseISO, isAfter, isBefore, isEqual, format } from 'date-fns';
import type { DateRange } from 'react-day-picker';
import { CycleData, CyclePhase } from '@/utils/cycle-tracking';

export type SymptomLogEntry = {
  id: string;
  user_id: string;
  symptoms: string[];
  custom_symptoms?: string[];
  intensity_level?: number;
  logged_at: string;
  notes?: string;
};

export const useSymptomData = (userId: string | undefined) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [symptomData, setSymptomData] = useState<SymptomLogEntry[]>([]);
  const [cycleData, setCycleData] = useState<CycleData | null>(null);
  const [cyclePhaseSymptoms, setCyclePhaseSymptoms] = useState<Record<string, string[]>>({
    menstruation: [],
    follicular: [],
    ovulation: [],
    luteal: [],
    premenstrual: [],
    // Aliases
    menstrual: [],
    ovulatory: []
  });
  
  // Fetch symptom data
  useEffect(() => {
    const fetchSymptomData = async () => {
      if (!userId) return;
      
      setIsLoading(true);
      try {
        const { data, error } = await supabase
          .from('symptom_logs')
          .select('*')
          .eq('user_id', userId)
          .order('logged_at', { ascending: true });
        
        if (error) throw error;
        
        setSymptomData(data || []);
      } catch (error) {
        console.error('Error fetching symptom data:', error);
        toast.error('Failed to load your symptom data. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchSymptomData();
  }, [userId]);
  
  // Filter data by date range
  const filterDataByDateRange = (data: SymptomLogEntry[], range: DateRange | undefined) => {
    if (!range?.from || !range?.to) return data;
    
    return data.filter((item) => {
      const itemDate = parseISO(item.logged_at);
      return (
        (isEqual(itemDate, range.from) || isAfter(itemDate, range.from)) &&
        (isEqual(itemDate, range.to) || isBefore(itemDate, range.to))
      );
    });
  };
  
  // Get symptoms by cycle phase
  const getSymptomsByPhase = (phase: CyclePhase) => {
    return cyclePhaseSymptoms[phase] || [];
  };
  
  // Analyze symptom frequency by phase
  useEffect(() => {
    if (!cycleData || symptomData.length === 0) return;
    
    const phaseSymptoms: Record<string, Record<string, number>> = {
      menstruation: {},
      follicular: {},
      ovulation: {},
      luteal: {},
      premenstrual: {},
      // Aliases
      menstrual: {},
      ovulatory: {}
    };
    
    // Map each symptom log to its cycle phase
    symptomData.forEach(log => {
      const logDate = parseISO(log.logged_at);
      
      // Find the cycle day that matches this symptom log
      const cycleDay = cycleData.days.find(day => 
        day.absoluteDate && format(day.absoluteDate, 'yyyy-MM-dd') === format(logDate, 'yyyy-MM-dd')
      );
      
      if (cycleDay) {
        const phase = cycleDay.phase;
        
        // Count symptoms for this phase
        log.symptoms.forEach(symptom => {
          phaseSymptoms[phase][symptom] = (phaseSymptoms[phase][symptom] || 0) + 1;
        });
      }
    });
    
    // Convert to sorted arrays for each phase
    const phaseTopSymptoms: Record<string, string[]> = {
      menstruation: [],
      follicular: [],
      ovulation: [],
      luteal: [],
      premenstrual: [],
      // Aliases
      menstrual: [],
      ovulatory: []
    };
    
    Object.entries(phaseSymptoms).forEach(([phase, symptoms]) => {
      const sortedSymptoms = Object.entries(symptoms)
        .sort((a, b) => b[1] - a[1])
        .map(([symptom]) => symptom);
      
      phaseTopSymptoms[phase] = sortedSymptoms;
    });
    
    setCyclePhaseSymptoms(phaseTopSymptoms);
  }, [symptomData, cycleData]);
  
  return {
    symptomData,
    isLoading,
    filterDataByDateRange,
    cyclePhaseSymptoms,
    getSymptomsByPhase,
    setCycleData
  };
};
